/**
 * TimeEntryList Component
 *
 * Time entries list with date range filtering, project/status/type filters,
 * draft entry editing, batch submission, and pagination.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import type {
  TimeEntry,
  TimeEntryStatus,
  TimeEntryType,
  Project,
} from '@/types/contractors';
import { TIME_ENTRY_STATUS_CONFIG as STATUS_CONFIG } from '@/types/contractors';
import { timeEntriesApi, projectsApi } from '@/services/contractorsApi';

const PAGE_SIZE = 20;

interface TimeEntryListProps {
  contractorUserId?: number;
}

export function TimeEntryList({ contractorUserId }: TimeEntryListProps): React.JSX.Element {
  const { t } = useTranslation('contractors');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_superadmin === true;

  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [projectId, setProjectId] = useState('');
  const [statusFilter, setStatusFilter] = useState<TimeEntryStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<TimeEntryType | ''>('');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Fetch projects for filter dropdown
  useEffect(() => {
    projectsApi.list({ limit: 100 }).then((res) => {
      setProjects(Array.isArray(res) ? res : []);
    }).catch(() => { /* ignore */ });
  }, []);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (projectId) params.project_id = projectId;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.entry_type = typeFilter;
      if (!isAdmin && contractorUserId) params.contractor_user_id = contractorUserId;

      const result = await timeEntriesApi.list(params as Parameters<typeof timeEntriesApi.list>[0]);
      const items = Array.isArray(result) ? result : result;
      setEntries(Array.isArray(items) ? items : []);
      setTotal(Array.isArray(result) ? result.length : 0);
    } catch {
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [page, dateFrom, dateTo, projectId, statusFilter, typeFilter, isAdmin, contractorUserId, t]);

  useEffect(() => {
    void fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, projectId, statusFilter, typeFilter]);

  const handleSelectToggle = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmitSelected = async (): Promise<void> => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    try {
      await timeEntriesApi.submit(Array.from(selectedIds));
      setSelectedIds(new Set());
      await fetchEntries();
    } catch {
      setError(t('errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await timeEntriesApi.delete(id);
      await fetchEntries();
    } catch {
      setError(t('errors.deleteFailed'));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const draftEntries = entries.filter((e) => e.status === 'draft');
  const allDraftsSelected = draftEntries.length > 0 && draftEntries.every((e) => selectedIds.has(e.id));

  const handleSelectAllDrafts = (): void => {
    if (allDraftsSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(draftEntries.map((e) => e.id)));
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{t('timeEntries.title')}</h2>
        {selectedIds.size > 0 && (
          <button
            onClick={handleSubmitSelected}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? t('common.submitting') : t('timeEntries.submitSelected', { count: selectedIds.size })}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('timeEntries.dateFrom')}</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('timeEntries.dateTo')}</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('timeEntries.project')}</label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('common.all')}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('timeEntries.status')}</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as TimeEntryStatus | '')}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('common.all')}</option>
            <option value="draft">{t('status.draft')}</option>
            <option value="submitted">{t('status.submitted')}</option>
            <option value="approved">{t('status.approved')}</option>
            <option value="rejected">{t('status.rejected')}</option>
            <option value="invoiced">{t('status.invoiced')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('timeEntries.type')}</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TimeEntryType | '')}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('common.all')}</option>
            <option value="stt">STT</option>
            <option value="ett">ETT</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left">
                {draftEntries.length > 0 && (
                  <input
                    type="checkbox"
                    checked={allDraftsSelected}
                    onChange={handleSelectAllDrafts}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                )}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.date')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.hours')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.project')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.type')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.description')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('timeEntries.status')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">{t('common.loading')}</td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">{t('timeEntries.noEntries')}</td>
              </tr>
            ) : (
              entries.map((entry) => {
                const cfg = STATUS_CONFIG[entry.status];
                const isDraft = entry.status === 'draft';
                return (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3">
                      {isDraft && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(entry.id)}
                          onChange={() => handleSelectToggle(entry.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{entry.date}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{entry.hours}h</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.project_name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                        entry.entry_type === 'ett' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {entry.entry_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{entry.description || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${cfg.bgColor} ${cfg.color}`}>
                        {t(`status.${entry.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {isDraft && (
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          {t('common.delete')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {t('common.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              {t('common.previous')}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
